import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { API_BASE_URL } from '../../constants/api';

interface InsufficientTokensModalProps {
  onNavigate: (target: string) => void;
}

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('auth_token');
  return token
    ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    : { 'Content-Type': 'application/json' };
};

const formatTokens = (n: number) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toLocaleString();
};

const InsufficientTokensModal: React.FC<InsufficientTokensModalProps> = ({ onNavigate }) => {
  const [visible, setVisible] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [tokensUsed, setTokensUsed] = useState(0);
  const [tokensPurchased, setTokensPurchased] = useState(0);

  const show = useCallback(async () => {
    setVisible(true);
    try {
      const res = await fetch(`${API_BASE_URL}/organizations/my-org`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success && data.data) {
        setOrgName(data.data.name || '');
        setTokensUsed(data.data.billing?.tokens_used || 0);
        setTokensPurchased(data.data.billing?.tokens_purchased || 0);
      }
    } catch (e) { /* ignore */ }
  }, []);

  // Listen for custom event from any API call that gets 402
  useEffect(() => {
    const handler = () => show();
    window.addEventListener('insufficient-tokens', handler);
    return () => window.removeEventListener('insufficient-tokens', handler);
  }, [show]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md mx-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-100 rounded-full">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Out of Tokens</h3>
        </div>

        <p className="text-gray-600 mb-4">
          Your organization has used all its tokens. Add credits to continue validating.
        </p>

        {(orgName || tokensPurchased > 0) && (
          <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm space-y-1">
            {orgName && <div className="flex justify-between"><span className="text-gray-500">Organization:</span><span className="font-medium">{orgName}</span></div>}
            {tokensPurchased > 0 && (
              <>
                <div className="flex justify-between"><span className="text-gray-500">Tokens purchased:</span><span className="font-medium">{formatTokens(tokensPurchased)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Tokens used:</span><span className="font-medium text-red-600">{formatTokens(tokensUsed)}</span></div>
              </>
            )}
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={() => { setVisible(false); onNavigate('billing'); }}
            className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
            Add Credits
          </button>
          <button
            onClick={() => setVisible(false)}
            className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700">
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

export default InsufficientTokensModal;
